import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { Fulfillment } from 'api/models/Fulfillment';
import { ASC, AttioQueryKeys, DESC } from 'common/constants/analyst-dashboards';
import {
  FULFILLMENT_COMPANY_NAME_KEY,
  FULFILLMENT_DATE_KEY,
  FULFILLMENT_MODEL_DUE_DATE_KEY,
  FULFILLMENT_MODEL_STATUS_KEY,
  SortCriteria,
} from 'dashboard409a/common/constants/analyst-dashboard';
import { FilterTypeSelection, MODEL_FILTER_TYPES } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { loadBulkConsultants, loadBulkValuations } from 'dashboard409a/services/attio-dashboard';
import { AttioValuationAttributes } from 'dashboard409a/services/attio.types';
import { useModelsAll } from 'dashboard409a/services/models';
import {
  ModelWithValuationData,
  UseModelsWithValuationDataProps,
  UseModelsWithValuationDataReturn,
} from 'dashboard409a/services/models.types';
import { compareValuesForSort } from 'dashboard409a/utilities';
import { Logger } from 'utilities';

// Constants
const DEFAULT_ORDER_BY = FULFILLMENT_DATE_KEY;
const DEFAULT_ORDER = DESC;
const MODELS_SORT_KEY = 'modelsSort';
const BULK_VALUATION_BATCH_SIZE = 200;

// Default FilterTypeSelection (current only) – used when no filterTypeSelection is provided
const DEFAULT_FILTER_TYPE_SELECTION: FilterTypeSelection = {
  current: true,
  past: false,
  unassigned: false,
  all: false,
};

// Convert legacy filterType string to FilterTypeSelection
const filterTypeToSelection = (filterType: string): FilterTypeSelection => {
  switch (filterType) {
    case MODEL_FILTER_TYPES.CURRENT:
      return { current: true, past: false, unassigned: false, all: false };
    case MODEL_FILTER_TYPES.PAST:
      return { current: false, past: true, unassigned: false, all: false };
    case MODEL_FILTER_TYPES.UNASSIGNED:
      return { current: false, past: false, unassigned: true, all: false };
    case MODEL_FILTER_TYPES.ALL:
      return { current: true, past: true, unassigned: true, all: true };
    default:
      return DEFAULT_FILTER_TYPE_SELECTION;
  }
};

// Convert searchStatus string labels (e.g. 'Draft') to numeric model_status values
const searchStatusToModelStatuses = (searchStatus?: string[]): number[] => {
  if (!searchStatus || searchStatus.length === 0 || searchStatus.includes('All')) {
    return [];
  }
  return searchStatus
    .map(s => modelStatusMap[s as keyof typeof modelStatusMap])
    .filter((v): v is number => v !== undefined);
};

function extractValuationDate(valuation: AttioValuationAttributes): string {
  if (typeof valuation.valuation_date === 'string') {
    return valuation.valuation_date;
  }
  if (Array.isArray(valuation.valuation_date) && valuation.valuation_date[0]?.value) {
    return valuation.valuation_date[0].value;
  }
  return '-';
}

function extractValuationAttributes(valuation: AttioValuationAttributes): {
  valuationDate: string;
  valuationStatus: string;
  engagementType: string;
  valuationLeadRecordId: string | undefined;
} {
  const valuationDate = extractValuationDate(valuation);

  let valuationStatus = '-';
  const stageValue = (valuation as AttioValuationAttributes & { stage?: string }).stage;
  if (typeof stageValue === 'string' && stageValue && stageValue !== '-') {
    valuationStatus = stageValue;
  } else if (Array.isArray(valuation.stage) && valuation.stage[0]?.option?.title) {
    valuationStatus = valuation.stage[0].option.title;
  }

  let engagementType: string;
  if (typeof valuation.engagement_type === 'string' && valuation.engagement_type) {
    engagementType = valuation.engagement_type;
  } else if (Array.isArray(valuation.engagement_type) && valuation.engagement_type[0]?.option?.title) {
    engagementType = valuation.engagement_type[0].option.title;
  } else {
    engagementType = '-';
  }

  let valuationLeadRecordId: string | undefined;
  if (typeof valuation.valuation_lead_94 === 'string' && valuation.valuation_lead_94) {
    valuationLeadRecordId = valuation.valuation_lead_94;
  } else if (Array.isArray(valuation.valuation_lead_94) && valuation.valuation_lead_94[0]?.target_record_id) {
    valuationLeadRecordId = valuation.valuation_lead_94[0].target_record_id;
  }

  return { valuationDate, valuationStatus, engagementType, valuationLeadRecordId };
}

type ValuationBatchData = {
  valuationDate: string;
  engagementType: string;
  valuationStatus: string;
  valuationLeadRecordId?: string;
};

function applyBatchUpdateToModels(
  prev: ModelWithValuationData[] | null,
  batchValuationIds: Set<string>,
  batchValuationDataMap: Map<string, ValuationBatchData>,
  valuationLeadsMap: Map<string, { name: string; imageSrc: string }>
): ModelWithValuationData[] | null {
  if (!prev) return prev;
  return prev.map(model => {
    if (!model.attio_valuation_id || !batchValuationIds.has(model.attio_valuation_id)) {
      return model;
    }
    const valuationData = batchValuationDataMap.get(model.attio_valuation_id);
    if (!valuationData) {
      return {
        ...model,
        valuation_date: '-',
        engagement_type: '-',
        valuation_status: '-',
        valuation_lead: '-',
        valuation_lead_image_src: '-',
        is_loading_valuation_data: false,
      };
    }
    const leadData = valuationData.valuationLeadRecordId
      ? valuationLeadsMap.get(valuationData.valuationLeadRecordId) ?? { name: '-', imageSrc: '-' }
      : { name: '-', imageSrc: '-' };
    return {
      ...model,
      valuation_date: valuationData.valuationDate,
      engagement_type: valuationData.engagementType,
      valuation_status: valuationData.valuationStatus,
      valuation_lead: leadData.name,
      valuation_lead_image_src: leadData.imageSrc,
      is_loading_valuation_data: false,
    };
  });
}

function clearLoadingForUnmappedModels(
  prev: ModelWithValuationData[] | null
): ModelWithValuationData[] | null {
  if (!prev) return prev;
  return prev.map(model => {
    if (model.attio_valuation_id) {
      return model;
    }
    return { ...model, valuation_status: '-', is_loading_valuation_data: false };
  });
}

async function processBatch(
  batch: string[],
  signal: AbortSignal,
  queryClient: QueryClient,
  setDisplayedModels: Dispatch<SetStateAction<ModelWithValuationData[] | null>>
): Promise<void> {
  if (signal.aborted) return;

  const valuationsMap = await loadBulkValuations(batch, signal).catch(
    () => new Map<string, AttioValuationAttributes>()
  );
  if (signal.aborted) return;

  const batchValuationDataMap = new Map<string, ValuationBatchData>();
  const batchLeadIds: string[] = [];

  valuationsMap.forEach((valuation, valuationId) => {
    const { valuationDate, valuationStatus, engagementType, valuationLeadRecordId } =
      extractValuationAttributes(valuation);

    batchValuationDataMap.set(valuationId, { valuationDate, engagementType, valuationStatus, valuationLeadRecordId });
    queryClient.setQueryData([AttioQueryKeys.VALUATION_DATE, valuationId], valuationDate);
    queryClient.setQueryData([AttioQueryKeys.ENGAGEMENT_TYPE, valuationId], engagementType);
    queryClient.setQueryData([AttioQueryKeys.VALUATION_STATUS, valuationId], valuationStatus);

    if (valuationLeadRecordId) {
      queryClient.setQueryData([AttioQueryKeys.VALUATION_LEAD_RECORD_ID, valuationId], valuationLeadRecordId);
      batchLeadIds.push(valuationLeadRecordId);
    }
  });

  if (signal.aborted) return;

  const uniqueLeadIds = [...new Set(batchLeadIds)];
  const valuationLeadsMap =
    uniqueLeadIds.length > 0
      ? await loadBulkConsultants(uniqueLeadIds, signal)
      : new Map<string, { name: string; imageSrc: string }>();

  if (signal.aborted) return;

  valuationLeadsMap.forEach((data, id) => {
    queryClient.setQueryData([AttioQueryKeys.VALUATION_LEAD, id], data);
  });

  const batchValuationIds = new Set(batch);
  setDisplayedModels(prev =>
    applyBatchUpdateToModels(prev, batchValuationIds, batchValuationDataMap, valuationLeadsMap)
  );
}

export const useModelsWithValuationData = ({
  filterType,
  filterTypeSelection: filterTypeSelectionProp,
  searchStatus,
  preparerUserIds,
  reviewerUserIds,
  orderBy = FULFILLMENT_DATE_KEY,
  order = DESC,
  sortCriteria,
  fromDate,
  toDate,
  companyName,
}: UseModelsWithValuationDataProps): UseModelsWithValuationDataReturn => {
  // Resolve filterTypeSelection: prefer the explicit prop, fall back to deriving it from filterType
  const filterTypeSelection = filterTypeSelectionProp ?? filterTypeToSelection(filterType);

  // Convert searchStatus labels to numeric model_status values for backend filtering
  const modelStatuses = useMemo(() => searchStatusToModelStatuses(searchStatus), [searchStatus]);

  // Determine the actual sort order to use (from props, localStorage, or defaults)
  const actualSortOrder = useMemo(() => {
    const localSort = localStorage.getItem(MODELS_SORT_KEY);

    if (orderBy !== DEFAULT_ORDER_BY || order !== DEFAULT_ORDER) {
      return { sortBy: orderBy, sortOrder: order };
    }

    if (localSort) {
      try {
        const parsed = JSON.parse(localSort);
        return { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder };
      } catch {
        return { sortBy: orderBy, sortOrder: order };
      }
    }

    return { sortBy: orderBy, sortOrder: order };
  }, [orderBy, order]);

  // Fetch ALL models in a single request — backend returns every matching record at once
  const {
    data: models,
    isLoading: isLoadingModels,
    error: modelsError,
    refetch: refetchModels,
  } = useModelsAll({
    filterTypeSelection,
    preparerUserIds,
    reviewerUserIds,
    fromDate,
    toDate,
    companyName,
    modelStatuses,
  });

  const queryClient = useQueryClient();

  // displayedModels holds the models enhanced with (potentially cached) valuation data
  const [displayedModels, setDisplayedModels] = useState<ModelWithValuationData[] | null>(null);
  const [valuationDataLoaded, setValuationDataLoaded] = useState(false);
  const bulkAbortControllerRef = useRef<AbortController | null>(null);

  // Ref to the models snapshot that the Attio loading effect should use.
  // This is set once when models arrive and cleared on filter change.
  // Using a ref (not state) avoids including it in the loading effect's dep array,
  // which would cause the effect to re-run (and its cleanup to abort the load) whenever
  // setDisplayedModels is called inside the progressive batch loop.
  const modelsForValuationRef = useRef<ModelWithValuationData[]>([]);

  // Enrich incoming models with any valuation data already in the React Query cache
  const modelsWithValuationData = useMemo(() => {
    if (!models || models.length === 0) {
      return null;
    }

    return models.map((model: ModelWithValuationData) => {
      const cachedValuationDate = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.VALUATION_DATE, model.attio_valuation_id])
        : undefined;
      const cachedEngagementType = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.ENGAGEMENT_TYPE, model.attio_valuation_id])
        : undefined;
      const cachedValuationStatus = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.VALUATION_STATUS, model.attio_valuation_id])
        : undefined;
      const cachedValuationLeadRecordId = model.attio_valuation_id
        ? queryClient.getQueryData<string>([AttioQueryKeys.VALUATION_LEAD_RECORD_ID, model.attio_valuation_id])
        : undefined;

      let cachedValuationLead: string | undefined;
      let cachedValuationLeadImageSrc: string | undefined;
      if (cachedValuationLeadRecordId) {
        const cachedLeadData = queryClient.getQueryData<{ name: string; imageSrc: string }>([
          AttioQueryKeys.VALUATION_LEAD,
          cachedValuationLeadRecordId,
        ]);
        if (cachedLeadData) {
          cachedValuationLead = cachedLeadData.name;
          cachedValuationLeadImageSrc = cachedLeadData.imageSrc;
        }
      }

      const hasCachedData = cachedValuationDate !== undefined || cachedValuationStatus !== undefined;
      const needsValuationData = !hasCachedData || cachedValuationStatus === undefined;

      return {
        ...model,
        is_loading_valuation_data: needsValuationData,
        valuation_date: cachedValuationDate ?? undefined,
        engagement_type: cachedEngagementType ?? undefined,
        valuation_status: cachedValuationStatus ?? undefined,
        valuation_lead: cachedValuationLead ?? undefined,
        valuation_lead_image_src: cachedValuationLeadImageSrc ?? undefined,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  // Sync displayedModels + modelsForValuationRef when models arrive from the API.
  // modelsWithValuationData only changes when the API responds (new filter set) or on mount.
  useEffect(() => {
    if (modelsWithValuationData && modelsWithValuationData.length > 0) {
      modelsForValuationRef.current = modelsWithValuationData;
      setDisplayedModels(modelsWithValuationData);
    } else if (!isLoadingModels) {
      modelsForValuationRef.current = [];
      setDisplayedModels([]);
    }
  }, [modelsWithValuationData, isLoadingModels]);

  // Build a stable key that identifies the current filter set
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        filterTypeSelection,
        preparerUserIds: [...(preparerUserIds ?? [])].sort((a, b) => a - b),
        reviewerUserIds: [...(reviewerUserIds ?? [])].sort((a, b) => a - b),
        fromDate: fromDate ?? null,
        toDate: toDate ?? null,
        companyName: companyName ?? null,
        modelStatuses,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterTypeSelection, preparerUserIds, reviewerUserIds, fromDate, toDate, companyName, modelStatuses]
  );

  // Reset Attio loading state when filters change
  useEffect(() => {
    setValuationDataLoaded(false);
    modelsForValuationRef.current = [];
    if (bulkAbortControllerRef.current) {
      bulkAbortControllerRef.current.abort();
      bulkAbortControllerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Progressive Attio valuation loading.
  //
  // IMPORTANT: this effect depends on `modelsWithValuationData` (stable after API responds),
  // NOT on `displayedModels`. This is intentional: `setDisplayedModels` is called inside the
  // async batch loop after each batch of 200 IDs returns. If `displayedModels` were a dep,
  // React would run the effect's cleanup (which aborts the load) on every batch update.
  // By depending on `modelsWithValuationData` instead, the batch-level state updates do NOT
  // re-trigger this effect — only a genuine filter change (new API response) does.
  useEffect(() => {
    const currentModels = modelsForValuationRef.current;
    if (!currentModels || currentModels.length === 0 || valuationDataLoaded) {
      return;
    }

    const needsValuationData = currentModels.some(m => m.attio_valuation_id && m.is_loading_valuation_data);
    if (!needsValuationData) {
      setValuationDataLoaded(true);
      return;
    }

    const abortController = new AbortController();
    bulkAbortControllerRef.current = abortController;
    const { signal } = abortController;

    const valuationIds = [
      ...new Set(currentModels.filter(m => m.attio_valuation_id).map(m => m.attio_valuation_id)),
    ] as string[];

    const batches: string[][] = [];
    for (let i = 0; i < valuationIds.length; i += BULK_VALUATION_BATCH_SIZE) {
      batches.push(valuationIds.slice(i, i + BULK_VALUATION_BATCH_SIZE));
    }

    (async () => {
      try {
        // Run all batches concurrently — each batch fetches valuations then its own consultants
        await Promise.all(
          batches.map(batch => processBatch(batch, signal, queryClient, setDisplayedModels))
        );

        if (!signal.aborted) {
          // Clear skeleton for any models without an attio_valuation_id
          setDisplayedModels(clearLoadingForUnmappedModels);
          setValuationDataLoaded(true);
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          Logger.error('Error loading valuation data', error);
        }
      } finally {
        if (bulkAbortControllerRef.current === abortController) {
          bulkAbortControllerRef.current = null;
        }
      }
    })();

    return () => {
      abortController.abort();
      if (bulkAbortControllerRef.current === abortController) {
        bulkAbortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsWithValuationData, valuationDataLoaded, queryClient]);

  const updateModelNote = useCallback((modelId: number, noteContent: string, noteId?: number) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      return prev.map(model =>
        model.id === modelId
          ? {
            ...model,
            note: {
              id: noteId ?? model.note?.id ?? 0,
              content: noteContent,
              author_id: model.note?.author_id || 0,
              author_email: model.note?.author_email || '',
              author_name: model.note?.author_name || '',
              created_at: model.note?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          }
          : model
      );
    });
  }, []);

  const updateModelStatus = useCallback((modelId: number, newStatus: number) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      return prev.map(model =>
        model.id === modelId ? { ...model, model_status: newStatus as Fulfillment.model_status } : model
      );
    });
  }, []);

  const updateModelDueDate = useCallback((modelId: number, newDueDate: string) => {
    setDisplayedModels(prev => {
      if (!prev) {
        return prev;
      }
      return prev.map(model =>
        model.id === modelId ? { ...model, supporting_due_date: newDueDate } : model
      );
    });
  }, []);

  const getModelValue = (model: ModelWithValuationData, property: string): string | number => {
    switch (property) {
      case FULFILLMENT_COMPANY_NAME_KEY:
        return model.company_name || '';
      case FULFILLMENT_MODEL_STATUS_KEY:
        return model.model_status || 0;
      case FULFILLMENT_MODEL_DUE_DATE_KEY: {
        const raw = model.supporting_due_date as string | undefined;
        if (!raw) {
          return 0;
        }
        const ms = new Date(raw).getTime();
        return Number.isNaN(ms) ? 0 : ms;
      }
      case FULFILLMENT_DATE_KEY:
        return model.valuation_date || '';
      default:
        return ((model as unknown as Record<string, unknown>)[property] as string) || '';
    }
  };

  const sortModelsWithMultipleCriteria = useCallback(
    (modelsToSort: ModelWithValuationData[], criteria: SortCriteria[]): ModelWithValuationData[] => {
      if (criteria.length === 0) {
        return [...modelsToSort];
      }

      localStorage.setItem(MODELS_SORT_KEY, JSON.stringify({ sortCriteria: criteria }));

      return [...modelsToSort].sort((a, b) =>
        criteria.reduce((result, { property, direction }) => {
          if (result !== 0) {
            return result;
          }
          return compareValuesForSort(getModelValue(a, property), getModelValue(b, property), direction);
        }, 0)
      );
    },
    []
  );

  const sortedModels = useMemo(() => {
    if (!displayedModels) {
      return [];
    }

    if (sortCriteria && sortCriteria.length > 0) {
      return sortModelsWithMultipleCriteria(displayedModels, sortCriteria);
    }

    const { sortBy, sortOrder } = actualSortOrder;
    localStorage.setItem(MODELS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));

    return [...displayedModels].sort((a, b) => {
      const aValue = getModelValue(a, sortBy);
      const bValue = getModelValue(b, sortBy);

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const cmp = aValue.localeCompare(bValue);
        return sortOrder === ASC ? cmp : -cmp;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const cmp = aValue - bValue;
        return sortOrder === ASC ? cmp : -cmp;
      }
      const cmp = String(aValue).localeCompare(String(bValue));
      return sortOrder === ASC ? cmp : -cmp;
    });
  }, [displayedModels, actualSortOrder, sortCriteria, sortModelsWithMultipleCriteria]);

  const shouldShowLoading = isLoadingModels || displayedModels === null;

  return {
    rows: sortedModels,
    totalCount: sortedModels.length,
    isLoading: shouldShowLoading,
    isLoadingMore: false,
    isError: !!modelsError,
    error: modelsError,
    updateModelNote,
    updateModelStatus,
    updateModelDueDate,
    refetch: refetchModels,
  };
};
